import React, { useCallback, useRef, useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { AlertTriangle, Loader2 } from 'lucide-react';
import './primitives.css';

/**
 * Confirmation for destructive or irreversible actions. Built on Radix
 * AlertDialog: focus is trapped, Escape cancels, focus returns to the
 * trigger, and the cancel button is focused first.
 *
 * Controlled:
 *   <ConfirmDialog
 *     open={open} onOpenChange={setOpen}
 *     title="Delete domain?" description="Breach results for acme.com are removed too."
 *     confirmLabel="Delete domain" destructive
 *     onConfirm={async () => { await api.delete(...); }}
 *   />
 * If onConfirm returns a promise the dialog shows a pending state and
 * closes when it resolves; if it rejects the dialog stays open (show the
 * error with a toast from the caller).
 *
 * Imperative (no state wiring), see useConfirm() below.
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}) {
  const [pending, setPending] = useState(false);

  const handleConfirm = async (e) => {
    e.preventDefault(); // keep open until onConfirm settles
    if (pending) return;
    try {
      const result = onConfirm?.();
      if (result && typeof result.then === 'function') {
        setPending(true);
        await result;
      }
      onOpenChange?.(false);
    } catch {
      /* caller reports the error; keep the dialog open so the user can retry or cancel */
    } finally {
      setPending(false);
    }
  };

  const handleOpenChange = (next) => {
    if (pending) return;
    if (!next) onCancel?.();
    onOpenChange?.(next);
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={handleOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="is-dialog-overlay" />
        <AlertDialog.Content className="is-dialog" data-testid="confirm-dialog">
          <div className="is-dialog-head">
            {destructive && (
              <span className="is-dialog-icon" aria-hidden="true">
                <AlertTriangle size={16} />
              </span>
            )}
            <div style={{ minWidth: 0 }}>
              <AlertDialog.Title className="is-dialog-title">{title}</AlertDialog.Title>
              {description ? (
                <AlertDialog.Description className="is-dialog-body">{description}</AlertDialog.Description>
              ) : (
                <AlertDialog.Description className="sr-only">{title}</AlertDialog.Description>
              )}
              {children && <div className="is-dialog-body">{children}</div>}
            </div>
          </div>
          <div className="is-dialog-actions">
            <AlertDialog.Cancel className="is-btn is-btn--secondary" disabled={pending} data-testid="confirm-dialog-cancel">
              {cancelLabel}
            </AlertDialog.Cancel>
            <AlertDialog.Action
              className={`is-btn ${destructive ? 'is-btn--danger' : 'is-btn--primary'}`}
              onClick={handleConfirm}
              disabled={pending}
              aria-busy={pending || undefined}
              data-testid="confirm-dialog-confirm"
            >
              {pending && <Loader2 className="is-spinner" aria-hidden="true" />}
              {confirmLabel}
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

/**
 * Promise-based confirm without wiring open state:
 *
 *   const [confirm, confirmDialog] = useConfirm();
 *   ...
 *   if (await confirm({ title: 'Archive 12 briefs?', confirmLabel: 'Archive', destructive: true })) archive();
 *   ...
 *   return <>{...}{confirmDialog}</>;
 */
export function useConfirm() {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        resolverRef.current?.(false);
        resolverRef.current = resolve;
        setState({ ...options });
      }),
    []
  );

  const settle = (value) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setState(null);
  };

  const dialog = (
    <ConfirmDialog
      open={Boolean(state)}
      onOpenChange={(next) => { if (!next) settle(false); }}
      title={state?.title}
      description={state?.description}
      confirmLabel={state?.confirmLabel}
      cancelLabel={state?.cancelLabel}
      destructive={state?.destructive}
      onConfirm={() => settle(true)}
    >
      {state?.body}
    </ConfirmDialog>
  );

  return [confirm, dialog];
}
